import { matchPath } from 'react-router-dom'

// Document titles for every route, in one table.
//
// Screen readers announce the document title on navigation, and it's what
// browser history and bookmarks show — so each page needs its own. Driving it
// from a central table (rather than a useEffect in each page) means a new route
// can't silently ship with no title, and the format stays consistent.
//
// Format is "dub — <Page>", brand first. The marketing homepage is the one
// exception — the bare site name is correct there.

export const SITE_NAME = 'dub'

// Matched with `end: true`, so the order here is for readability, not
// precedence — '/groups/:id' can't swallow '/groups/:id/:tab'.
const ROUTE_TITLES = [
  // Public marketing + storefront
  ['/', SITE_NAME],
  ['/shop', SITE_NAME],
  ['/merch', 'Merch'],
  ['/merch/products/:handle', 'Merch'],
  ['/merch/custom', 'Design Your Own'],
  ['/terms', 'Terms of Service'],
  ['/privacy', 'Privacy Policy'],
  ['/contact', 'Contact'],
  ['/group-email', 'Group Email'],

  // Auth
  ['/login', 'Log In'],
  ['/register', 'Sign Up'],
  ['/reset-password', 'Reset Password'],

  // Player app
  ['/home', 'Home'],
  ['/tournaments', 'Tournaments'],
  ['/tournaments/create', 'Create Tournament'],
  ['/tournaments/:id', 'Tournament'],
  ['/brackets/create', 'Create Bracket'],
  ['/bracket/:bracketId/seed', 'Seed Bracket'],
  ['/bracket/:bracketId/rounds', 'Round Schedule'],
  ['/bracket/:bracketId/picks', 'Make Picks'],
  ['/leaderboards', 'Leaderboards'],
  ['/groups', 'Groups'],
  ['/groups/:id', 'Group'],
  ['/groups/:id/:tab', 'Group'],
  ['/join/:groupId', 'Join Group'],
  ['/profile', 'Profile'],
  ['/profile/:userId', 'Profile'],
  ['/prizes', 'Prizes'],
  ['/settings', 'Settings'],
  ['/sports', 'Sports'],

  // Admin
  ['/admin/dashboard', 'Admin Dashboard'],
  ['/admin/tournaments/:id/edit', 'Edit Tournament'],
  ['/admin/tournaments/:id/published', 'Tournament Published'],
  ['/admin/players', 'Manage Players'],
  ['/admin/users', 'Manage Users'],
  ['/admin/groups', 'Manage Groups'],
  ['/admin/prizes', 'Manage Prizes'],
  ['/admin/discounts', 'Manage Discounts'],
  ['/dev/cards', 'Card States'],
  ['/dev/bracket', 'Bracket Sandbox'],
]

// Prefixes a page name with the site name. Exported so pages that know
// something more specific than the route can reuse the format — e.g. a product
// page substituting the real product name once it loads.
export function formatTitle(pageName) {
  const name = typeof pageName === 'string' ? pageName.trim() : ''
  return !name || name === SITE_NAME ? SITE_NAME : `${SITE_NAME} — ${name}`
}

// Falls back to the bare site name for anything unlisted (redirect-only paths,
// 404s), which is never wrong — just less descriptive.
export function titleForPath(pathname) {
  for (const [path, pageName] of ROUTE_TITLES) {
    if (matchPath({ path, end: true }, pathname)) return formatTitle(pageName)
  }
  return SITE_NAME
}
